// The maintainer inbox's two WRITES (arc 101 slice 11, plan doc 05) — the state
// half of `routes/DonationsReceived`, split out the way `createMachineRemove` is:
// a mutation with a confirm step, a per-row busy key and a failure notice is
// state, not rendering, and it unit-tests without a shell.
//
// Three rules:
//
//   - **Busy is keyed BY GIFT.** Two cards are two independent writes; a single
//     "which row is saving" slot would re-enable card A the moment card B
//     started, which is how a maintainer double-fires a decline.
//   - **Every write is followed by a re-READ.** A gift's state is the row, never
//     the echo — `reload()` after both, so a decline that parked the row and a
//     pick the ceiling refused both render what the server actually holds.
//   - **A decline CONFIRMS first.** It releases someone else's machine back to
//     them; a mis-click that silently ends a donation is not recoverable by the
//     maintainer alone.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { chooseDonationModel, type DonationRow, declineDonation } from './donations';
import { createKeyedMutation } from './keyed-mutation';
import { canAdminWrite } from './roles';

/**
 * Whether to offer this caller the inbox's two writes. Both routes
 * (`PATCH /v1/donations/:id/model`, `POST /v1/donations/:id/decline`) require
 * `write:donations`, which the API groups with billing in ADMIN_SCOPES — so the
 * gate is `canAdminWrite` (`lib/roles.ts`), the `canManageWebhooks` /
 * `canForceIssueActions` precedent. It was `canOwnerWrite`, which hid both
 * controls from the org admins the platform authorizes to run donations.
 */
export const canManageDonations = canAdminWrite;

export type DonationInboxController = {
  /** True while THIS gift has a write in flight. */
  readonly isBusy: (id: string) => boolean;
  /** The gift awaiting decline confirmation, or null. Feed to `<ConfirmDialog/>`. */
  readonly declining: () => DonationRow | null;
  /** Failure copy for the surface's `<Toast/>`, or null. */
  readonly error: () => string | null;
  readonly clearError: () => void;
  /** Open the decline confirm for a gift, clearing any previous failure. */
  readonly askDecline: (row: DonationRow) => void;
  /** Dismiss the confirm without declining. */
  readonly cancelDecline: () => void;
  /** Decline the gift under confirmation. A second call mid-flight is dropped. */
  readonly confirmDecline: () => void;
  /** Record the maintainer's model pick — `null` clears it. */
  readonly choose: (row: DonationRow, model: string | null) => void;
};

export function createDonationInbox(input: {
  readonly token: string | null;
  /** Re-read the inbox from page one — the authority for every row's state. */
  readonly reload: () => void;
}): DonationInboxController {
  const [target, setTarget] = createSignal<DonationRow | null>(null);
  // One busy key per GIFT, shared by both writes. The decline's copy is the
  // surface default; the model pick overrides it per call, which is the shape
  // this controller already carried as a `key` parameter.
  const write = createKeyedMutation({ fallback: t('donations.decline_error') });

  return {
    isBusy: write.isBusy,
    declining: target,
    error: write.error,
    clearError: write.clearError,
    askDecline: (row) => {
      write.clearError();
      setTarget(row);
    },
    cancelDecline: () => setTarget(null),
    confirmDecline: () => {
      const row = target();
      if (row === null) return;
      void write.run(row.id, async () => {
        // Closed INSIDE the call, which `run` enters synchronously once the guard
        // has passed. On the way in, not on the way out, because leaving the
        // dialog open would invite a second confirm for a gift whose first one is
        // still in flight — and a confirm the guard DROPPED (this gift's model
        // pick is still landing) must leave it open rather than swallow the
        // intent, which closing before the call did.
        setTarget(null);
        await declineDonation(row.id, input.token);
        input.reload();
      });
    },
    choose: (row, model) => {
      void write.run(
        row.id,
        async () => {
          await chooseDonationModel(row.id, model, input.token);
          input.reload();
        },
        t('donations.model_error'),
      );
    },
  };
}
